import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './InviteMembersSelect.css';
import { useI18n } from '../../i18n';

const InviteMembersSelect = ({
    users,
    selectedUserIds,
    onChange,
    onToggle,
    role = 'member',
    onRoleChange,
    error,
    loading,
    placeholder = null
}) => {
    const { t } = useI18n();
    const actualPlaceholder = placeholder || t('pages.teams.form.invite_placeholder');
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState({});

    // Calculate dropdown position based on trigger's viewport position
    const updateDropdownPosition = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                zIndex: 9999,
            });
        }
    }, []);

    // Update position when opening and on scroll/resize
    useEffect(() => {
        if (!isOpen) return;
        updateDropdownPosition();

        const handleScrollOrResize = () => updateDropdownPosition();
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [isOpen, updateDropdownPosition]);

    // Filter users based on search term
    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected users' names for display
    const selectedUsers = users.filter(user =>
        selectedUserIds.some(id => String(id) === String(user.id))
    );
    const displayText = selectedUsers.length > 0
        ? selectedUsers.map(u => u.name).join(', ')
        : '';

    // Handle toggle user selection
    const handleToggleUser = useCallback((e, userId) => {
        e.stopPropagation();
        e.preventDefault();

        const userIdStr = String(userId);

        if (onToggle) {
            onToggle(userIdStr);
        } else if (onChange) {
            const isSelected = selectedUserIds.some(id => String(id) === userIdStr);
            const newSelectedIds = isSelected
                ? selectedUserIds.filter(id => String(id) !== userIdStr)
                : [...selectedUserIds.map(id => String(id)), userIdStr];
            onChange(newSelectedIds);
        }

    }, [onToggle, onChange, selectedUserIds]);

    // Toggle dropdown open/close
    const handleTriggerClick = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            const isInsideTrigger = triggerRef.current && triggerRef.current.contains(event.target);
            const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
            if (!isInsideTrigger && !isInsideDropdown) {
                setIsOpen(false);
            }
        };

        // Use setTimeout to avoid closing immediately from the same click that opened it
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Render the dropdown as a portal to escape overflow:hidden containers
    const renderDropdown = () => {
        if (!isOpen) return null;

        const dropdownContent = (
            <div
                ref={dropdownRef}
                className="invite-select-dropdown"
                style={dropdownStyle}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Search input */}
                <div className="invite-select-search">
                    <input
                        type="text"
                        placeholder={actualPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                </div>

                {/* Users list */}
                <div className="invite-select-list">
                    {loading ? (
                        <div className="invite-select-loading">{t('common.status.loading')}</div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="invite-select-empty">
                            {searchTerm ? t('common.empty_states.no_results') : t('pages.teams.no_users_available_invite')}
                        </div>
                    ) : (
                        filteredUsers.map(user => {
                            const isSelected = selectedUserIds.some(id => String(id) === String(user.id));
                            return (
                                <div
                                    key={user.id}
                                    className={`invite-select-item ${isSelected ? 'selected' : ''}`}
                                    onClick={(e) => handleToggleUser(e, user.id)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <div className="invite-checkbox">
                                        {isSelected && (
                                            <svg viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="invite-user-name">{user.name}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );

        // Render as portal to escape overflow:hidden/auto containers
        return ReactDOM.createPortal(dropdownContent, document.body);
    };

    return (
        <div className="invite-members-select" ref={containerRef}>
            {/* Selected value display / trigger */}
            <div
                ref={triggerRef}
                className={`invite-select-trigger ${isOpen ? 'open' : ''} ${error ? 'has-error' : ''}`}
                onClick={handleTriggerClick}
            >
                <div className="invite-select-value">
                    {selectedUsers.length > 0 ? (
                        <span className="selected-names">{displayText}</span>
                    ) : (
                        <span className="placeholder-text">{actualPlaceholder}</span>
                    )}
                </div>

                {/* Role selector (only shown when users are selected) */}
                {onRoleChange && selectedUsers.length > 0 && (
                    <div className="role-selector" onClick={(e) => e.stopPropagation()}>
                        <select
                            value={role}
                            onChange={(e) => onRoleChange(e.target.value)}
                            className="role-dropdown"
                        >
                            <option value="member">{t('pages.teams.labels.member')}</option>
                            <option value="owner">{t('pages.teams.labels.owner')}</option>
                        </select>
                    </div>
                )}

                {/* Selected count badge */}
                {/* {selectedUserIds.length > 0 && (
                    <div className="invite-select-count-badge">
                        {selectedUserIds.length}
                    </div>
                )} */}

                <div className="invite-select-arrow">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>

            {/* Dropdown rendered as portal */}
            {renderDropdown()}

            {error && <div className="invite-select-error">{error}</div>}
        </div>
    );
};

export default InviteMembersSelect;
